import { PayloadAction } from '@reduxjs/toolkit';

import { DesignData } from 'editor/src/store/design/types';
import getDesignKey, { getDesignKeyFromDesign } from 'editor/src/store/variants/helpers/getDesignKey';
import { VariantsState } from 'editor/src/store/variants/types';

import reflectDesignData from 'editor/src/util/reflectDesignData';
import { ReflectContext } from 'editor/src/util/reflectDesignData/getReflectContext';

import { UNSELECTED_PAGE_COUNT } from './setPageCountReducer';

const setGroupDesignDataReducer = (
  state: VariantsState,
  action: PayloadAction<{
    groupKey: string;
    designData: DesignData | undefined;
    reflectContext: ReflectContext;
    // GAPI-47583 — the caller could not compute a design for this group because
    // a design template is missing: whatever designData the group holds is now
    // out of date. NOT set on a spread-count mismatch, where the group
    // legitimately keeps its own design (GAPI-39157).
    templatesMissing?: boolean;
  }>,
) => {
  const { groupKey, designData, reflectContext, templatesMissing } = action.payload;
  const group = state.variationGroups.find((group) => group.key === groupKey);
  if (group) {
    if (designData === undefined && templatesMissing) {
      group.variationsInfo.forEach((variationInfo) => {
        if (variationInfo.designData) {
          variationInfo.designDataStale = true;
        }
      });
      return;
    }

    const { selectedPageCount } = state;
    const designDataKey = designData ? getDesignKeyFromDesign(designData) : undefined;
    group.variationsInfo.forEach((variationInfo) => {
      const designKey = getDesignKey(variationInfo.variation.productUid, {
        ...variationInfo,
        pageCount: selectedPageCount === UNSELECTED_PAGE_COUNT ? undefined : selectedPageCount,
      });
      if (designKey === designDataKey) {
        variationInfo.designData = designData;
        variationInfo.designDataStale = undefined;
      } else {
        const template = state.designTemplates[designKey];
        // GAPI-39157: Don't overwrite existing designData when the template for
        // THIS variant has a different spread count than the incoming designData.
        // This happens when "Apply changes to all variants" propagates a 2-spread
        // design (two-sided flyer) onto a 1-spread variant (one-sided flyer).
        // reflectDesignData would produce output matching the template's spread
        // count, but we skip it entirely because the variant already has correct
        // designData and the incoming design is structurally incompatible.
        // Guard only fires when all three exist: prior designData, template, AND
        // incoming designData — if any is missing, we still want reflection to run.
        const spreadCountMismatch =
          variationInfo.designData && template && designData && template.spreads.length !== designData.spreads.length;
        if (!spreadCountMismatch) {
          const reflected =
            designData && template ? reflectDesignData(designData, template, reflectContext) : undefined;
          if (reflected !== undefined) {
            variationInfo.designData = reflected;
            variationInfo.designDataStale = undefined;
          } else if (designData && !template) {
            // GAPI-47583: the design moved on but this variant could not follow
            // because its template has not been fetched yet
            // (`checkForMissingTemplatesOperation` requests it, and
            // `applyTemplatesIfNeededOperation` applies the current design once
            // it lands). Until then its designData is out of date — flag it so
            // `useVariantWatcher` does not present the snapshot to the host as
            // current. NOT flagged on `spreadCountMismatch`: there the variant
            // legitimately keeps its own design (GAPI-39157).
            variationInfo.designDataStale = true;
          }
        }
      }
    });
  }
};

export default setGroupDesignDataReducer;
